import type { SVGProps } from 'react';
import { cn } from '../../utils/cn';
import { useDsT } from '../../services/i18n';

export interface SpinnerProps extends Omit<SVGProps<SVGSVGElement>, 'role'> {
  /** Diameter in px. */
  size?: number;
  /** Accessible label; defaults to "Loading". Ignored when the spinner is `aria-hidden`. */
  label?: string;
  className?: string;
}

/** Stroke width in user units that renders as 1.5 device px at the requested size. */
function strokeFor(size: number): number {
  return (24 / Math.max(1, size)) * 1.5;
}

/**
 * Indeterminate loading arc: a single 1.5px stroke in `currentColor` over a faint track.
 * Announced as `role="status"` unless it is rendered decoratively (`aria-hidden`), which is what
 * buttons do so the spinner never joins the button's accessible name.
 */
export function Spinner({ size = 16, label: labelProp, className, ...rest }: SpinnerProps) {
  const t = useDsT();
  const label = labelProp ?? t('ds.common.loading', 'Loading');
  const decorative = rest['aria-hidden'] === true || rest['aria-hidden'] === 'true';
  const stroke = strokeFor(size);
  return (
    <svg
      {...(decorative ? {} : { role: 'status', 'aria-label': label })}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={cn('zo-spinner shrink-0 animate-spin motion-reduce:animate-none', className)}
      {...rest}
    >
      <circle cx="12" cy="12" r="9.25" stroke="currentColor" strokeOpacity="0.2" strokeWidth={stroke} />
      <path d="M21.25 12A9.25 9.25 0 0 0 12 2.75" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
    </svg>
  );
}
